import { useState } from 'react';
import type { CSSProperties } from 'react';
import { Button, Col, Form, Input, Row, Space, Switch, Typography, theme } from 'antd';
import { PermissionCheckboxList } from './PermissionCheckboxList';
import { usePermissionsList } from '../../permissions/api/usePermissions';
import type { RoleResponse } from '../../../api/types';
import { useT } from '../../../locales';
import { summarizeSelectedPermissions, type PermissionSummary } from '../permission-metadata';
import '../roles.css';

export const ROLE_FORM_ID = 'role-policy-form';

export interface RoleFormValues {
  name: string;
  description: string;
  isActive: boolean;
  permissionIds: string[];
}

export interface RoleFormProps {
  mode: 'create' | 'edit';
  initialValues?: RoleResponse | null;
  onSubmit: (values: RoleFormValues) => void | Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  readOnly?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  onValuesChange?: (values: RoleFormValues) => void;
  onPermissionSummaryChange?: (summary: PermissionSummary) => void;
}

const DEFAULT_ROLE_FORM_VALUES: RoleFormValues = {
  name: '',
  description: '',
  isActive: true,
  permissionIds: [],
};

function getRoleFormValues(initialValues?: RoleResponse | null): RoleFormValues {
  if (!initialValues) return DEFAULT_ROLE_FORM_VALUES;

  return {
    name: initialValues.name,
    description: initialValues.description ?? '',
    isActive: initialValues.isActive,
    permissionIds: initialValues.permissions.map((permission) => permission.id),
  };
}

function normalizeRoleFormValues(values: Partial<RoleFormValues>): RoleFormValues {
  return {
    name: values.name ?? '',
    description: values.description ?? '',
    isActive: values.isActive ?? true,
    permissionIds: [...new Set(values.permissionIds ?? [])].sort(),
  };
}

function serializeRoleFormValues(values: Partial<RoleFormValues>): string {
  return JSON.stringify(normalizeRoleFormValues(values));
}

export function roleResponseToFormValues(role: RoleResponse): RoleFormValues {
  return getRoleFormValues(role);
}

export function RoleForm({
  mode,
  initialValues,
  onSubmit,
  onCancel,
  loading = false,
  readOnly = false,
  onDirtyChange,
  onValuesChange,
  onPermissionSummaryChange,
}: RoleFormProps) {
  const t = useT();
  const [form] = Form.useForm<RoleFormValues>();
  const [isDirty, setIsDirty] = useState(false);
  const { token } = theme.useToken();
  const isCreate = mode === 'create';
  const formInitialValues = getRoleFormValues(initialValues);
  const initialSnapshot = serializeRoleFormValues(formInitialValues);
  const {
    data: permissionsData,
    isLoading: isPermissionsLoading,
    error: permissionsError,
    refetch: refetchPermissions,
  } = usePermissionsList({ limit: 100 });
  const allPermissions = permissionsData?.data ?? [];

  const handleValuesChange = (
    _changedValues: Partial<RoleFormValues>,
    allValues: RoleFormValues,
  ) => {
    const normalizedValues = normalizeRoleFormValues(allValues);
    const nextIsDirty = serializeRoleFormValues(normalizedValues) !== initialSnapshot;
    setIsDirty(nextIsDirty);
    onDirtyChange?.(nextIsDirty);
    onValuesChange?.(normalizedValues);
    if (Object.prototype.hasOwnProperty.call(_changedValues, 'permissionIds')) {
      onPermissionSummaryChange?.(
        summarizeSelectedPermissions(normalizedValues.permissionIds, allPermissions),
      );
    }
  };

  return (
    <Form<RoleFormValues>
      id={ROLE_FORM_ID}
      className="role-form"
      form={form}
      layout="vertical"
      disabled={readOnly}
      initialValues={formInitialValues}
      onFinish={onSubmit}
      onValuesChange={handleValuesChange}
      style={
        {
          '--role-form-border': token.colorBorderSecondary,
          '--role-form-shadow': token.boxShadowSecondary,
        } as CSSProperties
      }
    >
      <section className="role-form__section" aria-labelledby="role-general-heading">
        <div className="role-form__section-heading">
          <Typography.Title level={3} id="role-general-heading">
            {t('pages.roles.generalSection')}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t('pages.roles.generalSectionDescription')}
          </Typography.Text>
        </div>

        <Row gutter={[16, 8]}>
          <Col xs={24} md={14}>
            <Form.Item
              name="name"
              label={t('pages.roles.name')}
              rules={[{ required: true, message: t('pages.roles.nameRequired') }]}
            >
              <Input
                maxLength={50}
                placeholder={t('pages.roles.namePlaceholder')}
                aria-label={t('pages.roles.name')}
                disabled={readOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item name="isActive" label={t('pages.roles.status')} valuePropName="checked">
              <Switch
                checkedChildren={t('pages.roles.active')}
                unCheckedChildren={t('pages.roles.inactive')}
                aria-label={t('pages.roles.status')}
                disabled={readOnly}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="description" label={t('pages.roles.description')}>
              <Input.TextArea
                rows={3}
                maxLength={255}
                showCount
                placeholder={t('pages.roles.descriptionPlaceholder')}
                aria-label={t('pages.roles.description')}
                disabled={readOnly}
              />
            </Form.Item>
          </Col>
        </Row>
      </section>

      <section className="role-form__section" aria-labelledby="role-permissions-heading">
        <div className="role-form__section-heading">
          <Typography.Title level={3} id="role-permissions-heading">
            {t('pages.roles.permissionEditorTitle')}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t('pages.roles.permissionEditorDescription')}
          </Typography.Text>
        </div>

        <Form.Item name="permissionIds" style={{ marginBottom: 0 }}>
          <PermissionCheckboxList
            allPermissions={allPermissions}
            loading={isPermissionsLoading}
            error={permissionsError}
            onRetry={() => void refetchPermissions()}
            readOnly={readOnly}
          />
        </Form.Item>
      </section>

      {isDirty && !readOnly && (
        <div
          className="role-form__sticky-actions"
          style={{
            background: token.colorBgContainer,
            borderColor: token.colorBorderSecondary,
            boxShadow: token.boxShadowSecondary,
          }}
        >
          <Typography.Text type="secondary">{t('pages.roles.unsavedChangesHint')}</Typography.Text>
          <Space wrap>
            {onCancel && (
              <Button onClick={onCancel} disabled={loading}>
                {t('pages.roles.discard')}
              </Button>
            )}
            <Button type="primary" htmlType="submit" form={ROLE_FORM_ID} loading={loading}>
              {isCreate ? t('pages.roles.create') : t('pages.roles.saveChanges')}
            </Button>
          </Space>
        </div>
      )}
    </Form>
  );
}
